import React, { useEffect, useRef, useState } from "react";
import profilePlaceholder from "./profile-svgrepo-com.svg";
import useAuthStore from "../../../store/useAuthStore";
import { getAgencyByLiferayId } from "../../../api/nebAgencyService";
import {
  getDataProviderByAgencyId,
  updateColumnDataProviderById,
} from "../../../api/nebDataProviderService";
import {
  fetchUserProfileImage,
  uploadUserImage,
} from "../../../api/userService";
import {
  Row,
  Col,
  Card,
  Form,
  InputGroup,
  Modal,
  Button,
  Table,
  Badge,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { toast, Bounce } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { updatePassword, validatePassword } from "../../../api/userService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import * as solidIcons from "@fortawesome/free-solid-svg-icons";

const ProfileEditAdmin = () => {
  const [profileData, setProfileData] = useState({
    name: "loading",
    profileImageUrl: profilePlaceholder, // Default placeholder image
  });

  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const user = useAuthStore((state) => state.user); // Zustand store for user data
  const logout = useAuthStore((state) => state.logout); // Zustand logout function
  const fileInputRef = useRef(null); // Ref for the hidden file input

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!user?.id) return;

      try {
        // Fetch the profile image as a base64-encoded string
        const profileImageBase64 = await fetchUserProfileImage();

        setProfileData({
          name: user.name,
          email: user.email,
          profileImageUrl: profileImageBase64 || profilePlaceholder, // Set base64 URL for image
        });
      } catch (error) {
        console.error("Error fetching profile data:", error);
        // logout(); // Handle token invalidation
      }
    };

    if (user?.id) {
      fetchProfileData();
    }
  }, [user, logout]);

  const toggleCurrentPasswordVisibility = () =>
    setShowCurrentPassword((prev) => !prev);
  const toggleNewPasswordVisibility = () => setShowNewPassword((prev) => !prev);  
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  // Trigger file input click
  const handleProfileImageClick = () => {
    fileInputRef.current.click(); // Trigger the hidden file input when the image is clicked
  };

  const handleCancel = () => {
    const confirmation = window.confirm(
      "Are you sure you want to cancel this process?"
    );
    if (confirmation) {
      toast.success(`The process have been cancel`);
      setTimeout(() => {
        navigate(`/neb-profile/view`);
      }, 2000);
    }
  };

  const handleSave = async () => {
    try {
      if (!user?.id) return;

      // Update password if new and confirm match
      if (password === confirmPassword) {
        const passwordData = {
          password: password,
          currentPassword: currentPassword,
        };
        const response = await updatePassword(passwordData);
        console.log("Password updated successfully:", response);
      } else {
        toast.error("Passwords do not match!", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        return;
      }

      toast.success(`Edit Profile success!`);
      setTimeout(() => {
        navigate(`/`);
      }, 2000);
    } catch (error) {
      console.error("Error saving profile changes:", error);

      // Parse the error response
      try {
        const errorData = error.response?.data; // Assuming error has a `response.data` field
        if (
          errorData?.message?.type ===
          "UserPasswordException.MustMatchCurrentPassword"
        ) {
          toast.error("Invalid Current Password. Please check and re-enter.", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
        } else {
          toast.error(
            errorData?.message?.title || "An error occurred. Please try again.",
            {
              position: "top-center",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light",
            }
          );
        }
      } catch (parseError) {
        console.error("Failed to parse error response:", parseError);
        toast.error("An unexpected error occurred. Please try again later.", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      }
    }
  };

  // Check if the passwords match while typing
  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setPasswordMatch(e.target.value === password);
  };

  // Handle file selection and upload
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const formData = new FormData();
        formData.append("image", file);

        // Upload the image
        const response = await uploadUserImage(formData);
        if (response.status == 200) {
          const updatedProfileImageBase64 = await fetchUserProfileImage(); // Fetch the updated profile image
          setProfileData((prevState) => ({
            ...prevState,
            profileImageUrl: updatedProfileImageBase64 || profilePlaceholder,
          }));
        }
      } catch (error) {
        console.error("Error uploading profile image:", error);
      }
    }
  };

  const { name, email, profileImageUrl } = profileData;

  return (
    <Card className="mt-4">
      <Card.Body>
        <Row>
          {/* Profile Image */}
          <Col className="text-center">
            <img
              src={profileImageUrl}
              alt="Profile"
              className="profile-image mb-3"
              style={{
                width: "150px",
                height: "150px",
                borderRadius: "50%",
                cursor: "pointer",
              }}
              onClick={handleProfileImageClick}
            />
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
              accept="image/*"
            />
            <h3 className="profile-name">{name}</h3>
          </Col>
        </Row>

        {/* Change Password Section */}
        <Row className="mt-4">
          <Col>
            <Badge pill text="white" className="badge-color1 text-md">
              <FontAwesomeIcon icon={solidIcons["faTags"]} className="mr-2" />{" "}
              Change Password
            </Badge>
          </Col>
        </Row>

        <Form className="mt-4">
          <Form.Group className="row mb-2">
            <Form.Label htmlFor="currentPassword" className="col-sm-3 col-lg-2">
              Current Password:
            </Form.Label>
            <Col className="col-sm-9 col-lg-10">
              <InputGroup>
                <Form.Control
                  type={showCurrentPassword ? "text" : "password"}
                  placeholder="Current Password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="off"
                />
                <InputGroup.Text
                  onClick={toggleCurrentPasswordVisibility}
                  style={{ cursor: "pointer" }}
                >
                  <FontAwesomeIcon
                    icon={showCurrentPassword ? faEyeSlash : faEye}
                  />
                </InputGroup.Text>
              </InputGroup>
            </Col>
          </Form.Group>

          <Form.Group className="row mb-2">
            <Form.Label htmlFor="newPassword" className="col-sm-3 col-lg-2">
              New Password:
            </Form.Label>
            <Col className="col-sm-9 col-lg-10">
              <InputGroup>
                <Form.Control
                  type={showNewPassword ? "text" : "password"}
                  placeholder="New Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <InputGroup.Text
                  onClick={toggleNewPasswordVisibility}
                  style={{ cursor: "pointer" }}
                >
                  <FontAwesomeIcon
                    icon={showNewPassword ? faEyeSlash : faEye}
                  />
                </InputGroup.Text>
              </InputGroup>
            </Col>
          </Form.Group>

          <Form.Group className="row mb-2">
            <Form.Label htmlFor="confirmPassword" className="col-sm-3 col-lg-2">
              Confirm New Password:
            </Form.Label>
            <Col className="col-sm-9 col-lg-10">
              <InputGroup>
                <Form.Control
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  isInvalid={!passwordMatch}
                />
                <InputGroup.Text
                  style={{ cursor: "pointer" }}
                  onClick={toggleConfirmPasswordVisibility}
                >
                  <FontAwesomeIcon
                    icon={showConfirmPassword ? faEyeSlash : faEye}
                  />
                </InputGroup.Text>
              </InputGroup>
            </Col>
          </Form.Group>
        </Form>

        {/* Save & Cancel Buttons */}
        <Row className="mt-4">
          <Col>
            <Button
              variant="primary"
              size="sm"
              className="mr-2"
              onClick={handleSave}
              disabled={!passwordMatch}
            >
              Save
            </Button>
            <Button variant="outline-primary" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
          </Col>
        </Row>
        <br />
      </Card.Body>
    </Card>
  );
};

export default ProfileEditAdmin;
